import { db, auth } from '../firebase';
import { doc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { RootState } from '../store';

const waitForAuth = async () => {
  // Wait for auth to be initialized and user to be signed in
  if (!auth.currentUser) {
    await new Promise<void>((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          unsubscribe();
          resolve();
        }
      });
    });
  }
};

export const createPresentationSession = async (initialState: RootState['presentation']): Promise<string> => {
  console.log('Starting to create presentation session...');
  
  try {
    await waitForAuth();
    console.log('Auth ready, proceeding with session creation');

    // Generate a session ID
    const sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    console.log('Generated session ID:', sessionId);

    // Create a reference to the document
    const sessionRef = doc(db, 'presentations', sessionId);
    console.log('Created document reference');

    // Prepare the data
    const sessionData = {
      ...initialState,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      createdBy: auth.currentUser?.uid || 'anonymous',
      connectedClients: 0
    };
    console.log('Preparing session data:', sessionData);

    // Write to Firestore
    await setDoc(sessionRef, sessionData);
    console.log('Successfully wrote session data to Firestore');

    return sessionId;
  } catch (error) {
    console.error('Error in createPresentationSession:', error);
    throw error;
  }
};

export const updatePresentationSession = async (
  sessionId: string,
  updates: Partial<RootState['presentation']>
): Promise<void> => {
  console.log('Updating presentation session:', sessionId);
  
  try {
    await waitForAuth();
    console.log('Auth ready, proceeding with session update');

    const sessionRef = doc(db, 'presentations', sessionId);
    const updateData = {
      ...updates,
      lastUpdated: new Date().toISOString(),
      updatedBy: auth.currentUser?.uid || 'anonymous',
    };
    
    await setDoc(sessionRef, updateData, { merge: true });
    console.log('Successfully updated session');
  } catch (error) {
    console.error('Error in updatePresentationSession:', error);
    throw error;
  }
};

export const subscribeToPresentationUpdates = (
  sessionId: string,
  onUpdate: (state: RootState['presentation']) => void
) => {
  const sessionRef = doc(db, 'presentations', sessionId);
  
  return onSnapshot(sessionRef, (doc) => {
    if (doc.exists()) {
      onUpdate(doc.data() as RootState['presentation']);
    }
  });
};

export const getPresentationSession = async (
  sessionId: string
): Promise<RootState['presentation'] | null> => {
  console.log('Getting presentation session:', sessionId);
  
  try {
    await waitForAuth();
    console.log('Auth ready, proceeding with session retrieval');

    const sessionRef = doc(db, 'presentations', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      console.log('Found session document');
      return sessionDoc.data() as RootState['presentation'];
    }
    
    console.log('Session document not found');
    return null;
  } catch (error) {
    console.error('Error in getPresentationSession:', error);
    throw error;
  }
};
